import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { DualProgress } from '@/components/ui/dual-progress'

export interface CallDistributionItem {
  duration: string
  qualification: number
  transferred: number
}

export interface CallDistribution {
  total: number
  distribution: CallDistributionItem[]
}

interface CallDurationsProps {
  callDurations: CallDistribution
}

export function CallDurations({ callDurations }: CallDurationsProps) {
  const getTotalCalls = (type: 'qualification' | 'transferred') => {
    return callDurations.distribution.reduce((sum, item) => sum + item[type], 0)
  }

  const qualificationTotal = getTotalCalls('qualification')
  const transferredTotal = getTotalCalls('transferred')

  const getProgressValue = (count: number, total: number) => {
    return total > 0 ? (count / total) * 100 : 0
  }

  return (
    <Card className="mb-4">
      <CardHeader className="pb-2">
        <CardTitle>Call Duration Distribution</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[120px]">Duration</TableHead>
              <TableHead className="w-[160px]">Qualification Calls</TableHead>
              <TableHead className="w-[160px]">Transferred Calls</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {callDurations.distribution.map((item, index) => {
              const qualProgress = getProgressValue(item.qualification, qualificationTotal)
              const transProgress = getProgressValue(item.transferred, transferredTotal)

              return (
                <TableRow key={index}>
                  <TableCell className="font-medium">{item.duration.replace('over', 'over ')}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-24">
                        {item.qualification}
                        {qualificationTotal > 0 && (
                          <span className="text-muted-foreground text-detail ml-1">
                            ({Math.round(qualProgress)}%)
                          </span>
                        )}
                      </div>
                    </div>
                  </TableCell>

                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="w-24">
                        {item.transferred}
                        {transferredTotal > 0 && (
                          <span className="text-muted-foreground text-detail ml-1">
                            ({Math.round(transProgress)}%)
                          </span>
                        )}
                      </div>
                    </div>
                  </TableCell>

                  <TableCell>
                    <div className="flex-1">
                      <DualProgress
                        qualificationValue={item.qualification}
                        transferredValue={item.transferred}
                        totalValue={qualificationTotal + transferredTotal}
                        className="h-2"
                      />
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
            <TableRow className="bg-muted/50">
              <TableCell className="font-semibold">Total</TableCell>
              <TableCell className="font-semibold">{qualificationTotal}</TableCell>
              <TableCell className="font-semibold">{transferredTotal}</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}
